import FingerprintJS from '@fingerprintjs/fingerprintjs'
import Toast from 'antd-mobile/es/components/toast'
import CryptoJS from 'crypto-js'

import {OsType} from '~/types/bussiness/callApp'

import {LOCAL_STORAGE_KEY_DID} from './constant'
import {getStorageItem, setStorageItem} from './storage'

/**
 * 获取浏览器指纹
 * @returns 浏览器指纹
 */
export async function getBrowserFingerprint() {
  const cachedFingerprint = getStorageItem(LOCAL_STORAGE_KEY_DID) || ''
  // 如果已经生成过指纹，直接返回
  if (cachedFingerprint) {
    return cachedFingerprint
  }

  // 初始化 FingerprintJS
  const fp = await FingerprintJS.load()
  // 获取指纹
  const result = await fp.get()
  // 缓存指纹
  setStorageItem(LOCAL_STORAGE_KEY_DID, result.visitorId)

  return result.visitorId
}

/**
 * 生成安全的 nonce
 * 优先级：Web Crypto API > crypto-js > 后备方案
 */
export const generateSecureNonce = async (): Promise<string> => {
  // 方案 1：使用 Web Crypto API（最安全）
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      const randomBytes = new Uint8Array(16)
      crypto.getRandomValues(randomBytes)

      // 转换为十六进制
      const hexString = Array.from(randomBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')

      return hexString
    } catch (error) {
      console.warn('Web Crypto API 生成 nonce 失败，使用后备方案', error)
    }
  }

  // 方案 2：使用 crypto-js（兼容性好）
  try {
    const randomString = [
      Math.random().toString(36),
      Date.now().toString(36),
      Math.random().toString(36),
      typeof performance !== 'undefined'
        ? performance.now().toString(36)
        : Math.random().toString(36),
    ].join('')

    const hashedNonce = CryptoJS.SHA256(randomString).toString(CryptoJS.enc.Hex)

    return hashedNonce.substring(0, 32)
  } catch (error) {
    console.error('生成 nonce 失败', error)
    // 最终后备方案
    return `${Date.now()}-${Math.random().toString(36)}`
  }
}

/**
 * 获取当前环境
 * @returns 'development' | 'production' | 'test'
 */
export const getEnvironment = (): 'development' | 'production' | 'test' => {
  console.log('ENV========>', ENV)
  return ENV as 'development' | 'production' | 'test'
}

/**
 * 判断是否是开发环境
 * @returns boolean
 */
export const isDevelopment = (): boolean => {
  return ENV === 'development'
}

/**
 * 判断是否是生产环境
 * @returns boolean
 */
export const isProduction = (): boolean => {
  return ENV === 'production'
}

/**
 * 使代码暂停指定的毫秒数
 * @param ms - 暂停的毫秒数
 * @returns Promise
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * 在下一个空闲时间执行回调函数
 * @param fn - 要执行的回调函数
 */
export const nextTick = (fn: IdleRequestCallback) => {
  const callback =
    typeof window !== 'undefined' &&
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (e: IdleRequestCallback) => {
          const n = Date.now()

          return setTimeout(() => {
            e({
              didTimeout: !1,
              timeRemaining() {
                return Math.max(0, 50 - (Date.now() - n))
              },
            })
          }, 1)
        }

  callback(fn, {timeout: 300})
}

/**
 * 检测移动端操作系统
 * @returns 'iOS' | 'Android' | 'Other'
 */
export const getMobileOS = (): OsType => {
  const {userAgent} = navigator
  // 检查iOS
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'ios'
  }

  // 检查Android
  if (/android/i.test(userAgent)) {
    return 'android'
  }

  // 其他环境
  return 'other'
}

/**
 * 判断是否在浏览器环境中运行
 * @returns boolean
 */
export function isBrowser() {
  return !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  )
}

/**
 * 分割数字
 * @param num 数字
 * @returns 分割后的数字
 */
export function splitPriceNumber(num: number) {
  // 将数字转换为字符串
  const numStr = num.toString()
  // 计算分割点
  const splitIndex = numStr.length - 2 // 取倒数第二位作为分割点

  // 分割字符串
  const part1 = numStr.slice(0, splitIndex) || '0'
  const part2 = numStr.slice(splitIndex)

  // 返回结果
  return [part1, part2]
}

export const blobToByteArray = async (blob: Blob) => {
  const buffer = await blob.arrayBuffer()
  return Array.from(new Uint8Array(buffer))
}

/**
 * 根据 MIME 类型获取文件扩展名
 * @param mimeType - MIME 类型字符串，如 'image/jpeg', 'application/pdf'
 * @returns 文件扩展名（不含点号），如 'jpg', 'png', 'pdf'，如果无法识别则返回空字符串
 */
export const getExtensionFromMimeType = (mimeType: string): string => {
  if (!mimeType) {
    return ''
  }

  // MIME 类型到扩展名的映射表
  const mimeToExt: Record<string, string> = {
    // 图片类型
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'image/x-icon': 'ico',
    'image/tiff': 'tiff',
    'image/tif': 'tif',

    // 文档类型
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'pptx',

    // 文本类型
    'text/plain': 'txt',
    'text/html': 'html',
    'text/css': 'css',
    'text/javascript': 'js',
    'text/json': 'json',
    'text/xml': 'xml',
    'text/csv': 'csv',
    'text/markdown': 'md',

    // 音频类型
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/aac': 'aac',
    'audio/flac': 'flac',
    'audio/webm': 'webm',

    // 视频类型
    'video/mp4': 'mp4',
    'video/mpeg': 'mpeg',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'video/webm': 'webm',
    'video/ogg': 'ogv',

    // 压缩文件
    'application/zip': 'zip',
    'application/x-rar-compressed': 'rar',
    'application/x-7z-compressed': '7z',
    'application/x-tar': 'tar',
    'application/gzip': 'gz',

    // 其他
    'application/json': 'json',
    'application/xml': 'xml',
    'application/octet-stream': 'bin',
  }

  // 转换为小写并去除空格
  const normalizedMimeType = mimeType.toLowerCase().trim()

  // 直接查找
  if (mimeToExt[normalizedMimeType]) {
    return mimeToExt[normalizedMimeType]
  }

  // 如果直接查找失败，尝试从 MIME 类型中提取（例如 'image/jpeg' -> 'jpeg'）
  const parts = normalizedMimeType.split('/')
  if (parts.length === 2) {
    const subtype = parts[1]
    // 处理一些特殊情况
    if (subtype.startsWith('x-')) {
      return subtype.substring(2)
    }
    // 对于常见的子类型，直接返回（如 'jpeg', 'png'）
    if (
      ['jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(subtype)
    ) {
      return subtype === 'jpeg' ? 'jpg' : subtype
    }
  }

  // 如果无法识别，返回空字符串
  return ''
}

/**
 * 获取图片的尺寸和方向信息
 * @param file - 图片文件（Blob 或 File）
 * @returns Promise<{width: number, height: number, orientation: number}>
 */
export const getImageInfo = (
  file: Blob | File
): Promise<{width: number; height: number; orientation: number}> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      const width = img.naturalWidth
      const height = img.naturalHeight

      // 读取EXIF方向信息
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer
          const view = new DataView(arrayBuffer)
          let orientation = 0

          // 检查是否为JPEG格式（通过文件头判断）
          if (view.getUint8(0) === 0xff && view.getUint8(1) === 0xd8) {
            // JPEG文件，查找EXIF数据
            let offset = 2
            while (offset < view.byteLength - 1) {
              if (
                view.getUint8(offset) === 0xff &&
                view.getUint8(offset + 1) === 0xe1
              ) {
                // 找到APP1段（EXIF数据通常在这里）
                const exifOffset = offset + 4

                // 检查是否为EXIF标识
                if (
                  view.getUint32(exifOffset, false) === 0x45786966 &&
                  view.getUint32(exifOffset + 4, false) === 0x00000000
                ) {
                  // 找到EXIF数据，查找Orientation标签
                  const tiffOffset = exifOffset + 8
                  const isLittleEndian =
                    view.getUint16(tiffOffset, false) === 0x4949

                  const ifdOffset = view.getUint32(
                    tiffOffset + 4,
                    isLittleEndian
                  )
                  const ifdAddress = tiffOffset + ifdOffset

                  const entryCount = view.getUint16(ifdAddress, isLittleEndian)

                  for (let i = 0; i < entryCount; i += 1) {
                    const entryOffset = ifdAddress + 2 + i * 12
                    const tag = view.getUint16(entryOffset, isLittleEndian)

                    // Orientation标签ID是274 (0x0112)
                    if (tag === 0x0112) {
                      orientation = view.getUint16(
                        entryOffset + 8,
                        isLittleEndian
                      )
                      break
                    }
                  }
                }
                break
              }
              // 移动到下一个段
              const segmentLength = view.getUint16(offset + 2, false)
              offset += 2 + segmentLength
            }
          }

          URL.revokeObjectURL(url)
          resolve({width, height, orientation: orientation || 0})
        } catch (error) {
          // 如果读取EXIF失败，使用默认值
          URL.revokeObjectURL(url)
          resolve({width, height, orientation: 0})
        }
      }

      reader.onerror = () => {
        URL.revokeObjectURL(url)
        // 如果读取失败，至少返回宽高
        resolve({width, height, orientation: 0})
      }

      // 读取文件的前64KB（通常EXIF数据在文件开头）
      const blob = file.slice(0, 65536)
      reader.readAsArrayBuffer(blob)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 */
export const copyTextToClipboard = async (text: string) => {
  try {
    if (!navigator.clipboard) {
      throw new Error('浏览器不支持Clipboard API')
    }
    await navigator.clipboard.writeText(text)
    Toast.show('复制成功')
  } catch (err) {
    // 回退到execCommand方案
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    Toast.show('已复制')
  }
}
